import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { FieldLabel, Headline, PageEyebrow, SplitShell } from '../components/auth-shells.js';
import { PasswordInput } from '../components/password-input.js';
import { PasswordStrength } from '../components/password-strength.js';
import { magToast } from '../components/toaster.js';
import { resetPassword } from '../lib/auth-client.js';

// Better Auth's reset link bounces here with ?token=… ; forgot-password
// passes the email as a second param so the subtitle can address the user
// by name (handoff §15.2.6).
const searchSchema = z.object({
  token: z.string().optional(),
  email: z.string().optional(),
});

export const Route = createFileRoute('/reset-password')({
  validateSearch: searchSchema,
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { t } = useLingui();

  const { token, email } = Route.useSearch();
  const navigate = useNavigate();

  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [pending, setPending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      magToast.error(
        i18n._(msg`this reset link is missing its token.`),
        i18n._(msg`request a new one.`),
      );
      return;
    }
    if (pw1 !== pw2) {
      magToast.warning(
        i18n._(msg`passwords don't match.`),
        i18n._(msg`the two fields need to be identical.`),
      );
      return;
    }
    setPending(true);
    const result = await resetPassword({ newPassword: pw1, token });
    if (result.error) {
      magToast.error(
        i18n._(msg`couldn't set the password.`),
        result.error.message ?? i18n._(msg`try again in a sec.`),
      );
      setPending(false);
      return;
    }
    navigate({ to: '/reset-password/success' });
  };

  return (
    <SplitShell quote={t`mag isn't grading. promise.`}>
      <PageEyebrow>
        <Trans>new password</Trans>
      </PageEyebrow>
      <div className="mt-3 flex flex-col gap-3">
        <Headline>
          <Trans>set a new one.</Trans>
        </Headline>
        <p className="text-[15px] leading-[1.5] text-[var(--ink)]/80">
          {email ? (
            <Trans>
              For <span className="font-semibold text-[var(--ink)]">{email}</span>.{' '}
            </Trans>
          ) : null}
          <Trans>At least 8 characters. Mag won't grade your password strength.</Trans>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mt-7 flex flex-col gap-4">
        {/* biome-ignore lint/a11y/noLabelWithoutControl: <PasswordInput> renders a real <input> — biome can't see through the component boundary. */}
        <label className="field">
          <FieldLabel>
            <Trans>new password</Trans>
          </FieldLabel>
          <PasswordInput
            value={pw1}
            onChange={setPw1}
            autoComplete="new-password"
            required
            placeholder={t`at least 8 characters`}
          />
        </label>
        {/* biome-ignore lint/a11y/noLabelWithoutControl: <PasswordInput> renders a real <input> — biome can't see through the component boundary. */}
        <label className="field">
          <FieldLabel>
            <Trans>confirm</Trans>
          </FieldLabel>
          <PasswordInput
            value={pw2}
            onChange={setPw2}
            autoComplete="new-password"
            required
            placeholder={t`same as above`}
          />
        </label>

        <PasswordStrength password={pw1} />

        <button type="submit" disabled={pending} className="btn mt-2 w-full disabled:opacity-60">
          {pending ? <Trans>saving…</Trans> : <Trans>save password</Trans>}
          <span className="btn-arrow">→</span>
        </button>

        <p className="mt-2 text-center text-[14px] text-[var(--ink)]/70">
          <Trans>
            link didn't work?{' '}
            <Link to="/forgot-password" className="link-plum">
              request a new one
            </Link>
          </Trans>
        </p>
      </form>
    </SplitShell>
  );
}
